import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ImportProgressState {
  importId: string | null;
  phase: 'initializing' | 'navigating' | 'loading' | 'extracting' | 'processing' | 'saving' | 'teams' | 'matches' | 'completed' | 'error';
  message: string;
  detail?: string; // Additional context for the current phase
  progress?: number; // Determinate progress (0-100) when available
  teamsProgress: {
    current: number;
    total: number;
    currentTeam?: string;
  };
  matchesProgress: {
    current: number;
    total: number;
    currentMatch?: string;
  };
  startTime: string | null; // ISO string
  estimatedCompletion: string | null; // ISO string
  elapsedTime: number;
  error: string | null;
  isConnected: boolean;
  isImporting: boolean;
  completedAt: string | null; // Track when import completed for auto-hide
}

const initialState: ImportProgressState = {
  importId: null,
  phase: 'initializing',
  message: '',
  detail: undefined,
  progress: undefined,
  teamsProgress: { current: 0, total: 0 },
  matchesProgress: { current: 0, total: 0 },
  startTime: null,
  estimatedCompletion: null,
  elapsedTime: 0,
  error: null,
  isConnected: false,
  isImporting: false,
  completedAt: null
};

const importSlice = createSlice({
  name: 'import',
  initialState,
  reducers: {
    startImport: (state, action: PayloadAction<{ importId: string }>) => {
      state.importId = action.payload.importId;
      state.phase = 'initializing';
      state.message = 'Initializing import...';
      state.detail = 'Setting up import environment';
      state.progress = 5;
      state.startTime = new Date().toISOString();
      state.error = null;
      state.isImporting = true;
      state.completedAt = null;
      // Reset progress
      state.teamsProgress = { current: 0, total: 0 };
      state.matchesProgress = { current: 0, total: 0 };
      state.elapsedTime = 0;
      state.estimatedCompletion = null;
    },
    
    updateProgress: (state, action: PayloadAction<Partial<ImportProgressState>>) => {
      // Update all provided fields, but preserve progress if not provided and we're in completed state
      const updates = action.payload;
      
      // If we're transitioning to completed state or already completed, ensure progress stays at 100
      if (updates.phase === 'completed' || (state.phase === 'completed' && updates.progress === undefined)) {
        updates.progress = 100;
      }
      
      Object.assign(state, updates);
      
      // Ensure isImporting is true during active phases
      const activePhases = ['initializing', 'navigating', 'loading', 'extracting', 'processing', 'saving', 'teams', 'matches'];
      if (activePhases.includes(state.phase)) {
        state.isImporting = true;
      } else if (state.phase === 'completed' || state.phase === 'error') {
        state.isImporting = false;
        if (state.phase === 'completed' && !state.completedAt) {
          state.completedAt = new Date().toISOString();
        }
      }
    },
    
    setConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
    },
    
    completeImport: (state) => {
      state.phase = 'completed';
      state.message = 'Import completed successfully!';
      state.progress = 100;
      state.isImporting = false;
      state.error = null;
      state.completedAt = new Date().toISOString();
    },
    
    errorImport: (state, action: PayloadAction<string>) => {
      state.phase = 'error';
      state.message = 'Import failed';
      state.detail = action.payload;
      state.progress = undefined;
      state.error = action.payload;
      state.isImporting = false;
    },
    
    hideCompletedImport: (state) => {
      // Hide completed import after delay
      if (state.phase === 'completed') {
        Object.assign(state, initialState);
      }
    },
    
    resetImport: (state) => {
      Object.assign(state, initialState);
    }
  }
});

export const {
  startImport,
  updateProgress,
  setConnectionStatus,
  completeImport,
  errorImport,
  hideCompletedImport,
  resetImport
} = importSlice.actions;

export default importSlice.reducer;
